/**
 * Owner-side two-way replies: list the replies recipients have sent back to the
 * vault owner, and open a letter reply by decrypting it on-device with the
 * owner's device key (the reply CEK was HPKE-wrapped to that key — the mirror of
 * the seal flow, so the same owner-device-key path used for senior review).
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - vendored JS has no types
import { unwrapCekForRecipient } from '../../../vendor/crypto/cek.js';

import type { ApiClient } from '../apiClient';
import { aesGcmDecrypt, base64ToBytes, loadOwnerPrivateKey } from '../ownerDeviceKey';

export interface ReplyListItem {
  id: string;
  source_message_id: string;
  format: 'video' | 'voice' | 'letter';
  sealed_at: string;
  viewed_at: string | null;
  recipient_full_name: string;
}

export interface ReplyDecryptContext {
  response_id: string;
  source_message_id: string;
  format: 'video' | 'voice' | 'letter';
  cek_ciphertext: string;
  iv_b64: string;
  letter_ciphertext_b64: string | null;
  r2_key: string | null;
}

export function listReplies(api: ApiClient, vaultId: string): Promise<{ responses: ReplyListItem[] }> {
  return api.get(`/api/vaults/${vaultId}/responses`);
}

export function getReplyDecryptContext(
  api: ApiClient,
  vaultId: string,
  responseId: string,
): Promise<ReplyDecryptContext> {
  return api.get(`/api/vaults/${vaultId}/responses/${responseId}/decrypt-context`);
}

/** Mark a reply viewed AFTER a successful on-device decrypt (best-effort). */
export function markReplyViewed(
  api: ApiClient,
  vaultId: string,
  responseId: string,
): Promise<{ viewed: true }> {
  return api.post(`/api/vaults/${vaultId}/responses/${responseId}/viewed`, {});
}

/**
 * Decrypt a LETTER reply on-device and return its text. The reply CEK is bound
 * (both HPKE info + AES-GCM AAD) to the SOURCE message id — the value the
 * recipient's `sealResponse` used — so the owner unwraps + decrypts against it.
 */
export async function decryptOwnerLetterReply(args: {
  api: ApiClient;
  vaultId: string;
  responseId: string;
  ownerEmail: string;
  privateKeyOverride?: CryptoKey;
}): Promise<{ text: string; sourceMessageId: string }> {
  const ctx = await getReplyDecryptContext(args.api, args.vaultId, args.responseId);
  if (ctx.format !== 'letter') {
    throw new Error('Only letter replies can be opened here yet.');
  }
  if (ctx.letter_ciphertext_b64 === null) {
    throw new Error('This reply has no readable text.');
  }
  const privKey = args.privateKeyOverride ?? (await loadOwnerPrivateKey(args.ownerEmail));
  const cek: Uint8Array = await unwrapCekForRecipient(ctx.cek_ciphertext, privKey, ctx.source_message_id);
  try {
    const plaintext = await aesGcmDecrypt({
      key: cek,
      iv: base64ToBytes(ctx.iv_b64),
      ciphertext: base64ToBytes(ctx.letter_ciphertext_b64),
      aad: new TextEncoder().encode(ctx.source_message_id),
    });
    return { text: new TextDecoder().decode(plaintext), sourceMessageId: ctx.source_message_id };
  } finally {
    cek.fill(0);
  }
}
